import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkIcon from "@mui/icons-material/Link";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { Contact } from "../../../interfaces/DTO/crm/contact";
import { simulationService } from "../../../services/SimulationService";
import { useAuthStore } from "../../../stores/authStore";
import { formatName, formatUniversalPhone } from "../../../utils/namesFormater";

const WHATSAPP_GREEN = "#25D366";

const WhatsAppBadge: React.FC<{ size: number }> = ({ size }) => (
  <Box
    sx={{
      bgcolor: WHATSAPP_GREEN,
      borderRadius: "50%",
      width: size,
      height: size,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    <WhatsAppIcon sx={{ fontSize: size * 0.65, color: "#fff" }} />
  </Box>
);

const buildMessage = (contact: Contact | undefined, title: string, link: string, agentName: string) =>
  [
    `היי ${contact?.first_name ?? ""},`.replace(" ,", ","),
    `הכנתי עבורך את הבקשות: ${title}`,
    "בלינק האישי אפשר לראות את הפרטים ולחתום:",
    link,
    "",
    agentName,
  ].join("\n");

interface Props {
  simulationId: number;
  contact?: Contact;
  /** the simulation's title - shown to the client on the link */
  title: string;
  actionsCount: number;
  onClose: () => void;
}

/**
 * אחרי יצירת המשימות: יצירת לינק חד פעמי ללקוח (לסימולציה), ואז שליחתו בוואטסאפ –
 * הודעה ניתנת לעריכה (כמו SharedDealWhatsAppDialog).
 */
const SimulationLinkSendStep: React.FC<Props> = ({ simulationId, contact, title, actionsCount, onClose }) => {
  const user = useAuthStore((s) => s.user);
  const agentName = (user && formatName(user)) || user?.username || "";

  const [link, setLink] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [phone, setPhone] = useState(contact?.phone ?? "");
  const [message, setMessage] = useState("");

  const createLink = async () => {
    setCreating(true);
    setError(null);
    try {
      const data = await simulationService.createPortalLink(simulationId);
      setLink(data.link);
      setMessage(buildMessage(contact, title, data.link, agentName));
    } catch (err: any) {
      setError(err?.response?.data?.detail || "שגיאה ביצירת לינק");
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!link) return;
    await navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleSend = () => {
    if (!phone) return;
    window.open(`https://wa.me/${formatUniversalPhone(phone)}?text=${encodeURIComponent(message)}`, "_blank");
  };

  const clientName = contact?.first_name ?? "ללקוח";

  return (
    <>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <Alert severity="success">
            נוצרה סימולציה "{title}" עם {actionsCount} פעולות
          </Alert>

          {error && <Alert severity="error">{error}</Alert>}

          {!link ? (
            <Typography variant="body2" color="text.secondary">
              הלינק החד פעמי מאפשר ללקוח לראות את הבקשות ולחתום עליהן. יצירת לינק חדש מבטלת לינק קודם של הסימולציה.
            </Typography>
          ) : (
            <>
              <TextField
                label="לינק ללקוח"
                value={link}
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={copied ? "הועתק!" : "העתק"}>
                        <IconButton onClick={handleCopy} edge="end" color={copied ? "success" : "default"}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                sx={{ direction: "ltr" }}
              />

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <WhatsAppBadge size={28} />
                <Typography variant="subtitle1" fontWeight={700}>
                  שליחה בוואטסאפ
                </Typography>
              </Box>
              <TextField
                label="טלפון"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                sx={{ maxWidth: 240, "& input": { direction: "ltr", textAlign: "right" } }}
                error={!phone}
                helperText={!phone ? "אין טלפון ללקוח – הזן מספר" : undefined}
              />
              <TextField multiline minRows={6} fullWidth value={message} onChange={(e) => setMessage(e.target.value)} />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" size="large">
          סגור
        </Button>
        {!link ? (
          <Button
            onClick={createLink}
            disabled={creating}
            variant="contained"
            size="large"
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <LinkIcon sx={{ ml: 1 }} />}
          >
            צור לינק חד פעמי ללקוח
          </Button>
        ) : (
          <Button
            onClick={handleSend}
            disabled={!phone}
            variant="contained"
            size="large"
            startIcon={
              <Box sx={{ ml: 1, display: "flex" }}>
                <WhatsAppBadge size={24} />
              </Box>
            }
          >
            שלח ל{clientName}
          </Button>
        )}
      </DialogActions>
    </>
  );
};

export default SimulationLinkSendStep;
